import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Container from "../Container";
import Context from "../../../context";
import './MyDocumentManager.scss';
import ListTitle from "../ListTitle";
import { useBlocker, useLocation, useNavigate } from "react-router-dom";
import { getBackPath } from "../../../helpers";
import TextInput from "../TextInput";
import Switch from '@mui/material/Switch';
import Button from "../Buttons/Button";
import ModalDefault from "../Modal/ModalDefault";
import { createInitialUploadDocConfig } from "./useDocumentUpload";
import Spinner from "../Spinner";
import { deleteDocument, saveDocumentInfo } from "../../../libraries/api";
import Modal from "../Modal";

const MyDocumentManager = ({ document, uploadDocConfig, setUploadDocConfig, handleFileUpload, myDocsUploadDetails }) => {
  const { viewMode, customHistory, itin, setItin, t, setGlobalModalContent } = useContext(Context);

  const docData = uploadDocConfig?.docData ?? document;

  const [initialState, setInitialState] = useState({
    docTitle: uploadDocConfig?.docData?.name ?? document?.name,
    restrictedClientIds: uploadDocConfig?.restrictedToTravellerIds ?? document?.restrictedToTravellerIds ?? null,
  });
  const [editModeRestrictedClientIds, setEditModeRestrictedClientIds] = useState(
    uploadDocConfig?.restrictedToTravellerIds ?? document?.restrictedToTravellerIds ?? null
  );
  const [docTitle, setDocTitle] = useState(uploadDocConfig?.docData?.name ?? document?.name);
  const [isModalConfirmDeletionOpen, setIsModalConfirmDeletionOpen] = useState(false);
  const [backButtonPath, setBackButtonPath] = useState(-1);
  const [spinner, setSpinner] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const backButton = useRef(null);

  const uiRestrictedClientIds = uploadDocConfig ? (uploadDocConfig?.restrictedToTravellerIds ?? null) : editModeRestrictedClientIds;

  const filteredClients = useMemo(() => {
    return itin.clients?.filter(client => client.id !== itin?.localData?.client?.id) ?? [];
  }, [itin?.clients, itin?.localData?.client?.id]);
  const travellerId = useMemo(() => itin?.clients?.find(client => client?.isCurrent)?.id, [itin?.clients]);

  const folderId = useMemo(() => {
    const urlParts = location.pathname.split('/');
    const docsIndex = urlParts.indexOf('document_settings');
    if (docsIndex === -1) return null;

    const segment = urlParts[docsIndex - 1];

    const docId = itin.documents?.find(doc => doc.pathName === segment)?.id;
    if (docId) return docId;

    const numericId = Number(segment);
    return !Number.isNaN(numericId) ? numericId : null;
  }, [itin.documents, location.pathname]);

  const hasUnsavedChanges = useMemo(() => {
    if (uploadDocConfig) return false;

    const titleChanged = docTitle !== initialState.docTitle;
    const restrictionsChanged = JSON.stringify([...(editModeRestrictedClientIds ?? [])].sort()) !== JSON.stringify([...(initialState.restrictedClientIds ?? [])].sort());

    return titleChanged || restrictionsChanged;
  }, [uploadDocConfig, docTitle, editModeRestrictedClientIds, initialState]);

  const blocker = useBlocker(hasUnsavedChanges);

  useLayoutEffect(() => {
    if (!document?.id) return;
    const backPath = getBackPath(location.pathname, customHistory, document.id ? 2 : null);
    setBackButtonPath(backPath);
  }, [customHistory, document?.id, location.pathname]);

  useEffect(() => {
    return () => {
      if (setUploadDocConfig) setUploadDocConfig(createInitialUploadDocConfig());
    };
  }, [setUploadDocConfig]);

  const saveDocInfo = useCallback(async body => {
    const { res, data, error } = await saveDocumentInfo(itin.operatorCode, itin.passcode, body, travellerId);
    if (!res.ok || error) {
      const internalErrMsg =  error || data?.error?.[0]?.message || data?.error || "Edit document info error";
      console.error('Editing document failed:', internalErrMsg)

      const msg = `${t("document_failed_to_edit")} ${docData.name}`;
      throw new Error(msg);
    }
    return data;
  }, [docData.name, itin.operatorCode, itin.passcode, t, travellerId]);

  const handleDocTitleChange = e => {
    const newTitle = e.target.value;
    setDocTitle(newTitle);

    if (uploadDocConfig) {
      setUploadDocConfig(prev => ({
        ...prev,
        docData: { ...prev.docData, name: newTitle },
      }));
    }
  };

  const applyRestrictedClientIdsUpdate = updater => {
    if (uploadDocConfig) {
      setUploadDocConfig(prev => ({
        ...prev,
        restrictedToTravellerIds: updater(prev.restrictedToTravellerIds),
      }));
    } else {
      setEditModeRestrictedClientIds(prev => updater(prev));
    }
  };

  const updateAndSyncDocRestrictions = (checked, id) => {
    applyRestrictedClientIdsUpdate(prevState => {
      const currentIds = prevState ?? [];
      if (checked) {
        return currentIds.includes(id) ? currentIds : [...currentIds, id];
      } else {
        return currentIds.filter(x => x !== id);
      }
    });
  };

  const handleChangeShareWithEveryone = () => {
    applyRestrictedClientIdsUpdate(prevState => {
      return prevState ? null : [travellerId];
    });
  };

  const handleEditDocument = async () => {
    setSpinner(true);
    try {
      const body = {
        folderId,
        fileUrl: docData.file.httpsUrl,
        restrictedToTravellerIds: editModeRestrictedClientIds,
        nodeId: docData.id,
        name: docTitle,
      };
      const updatedDoc = await saveDocInfo(body);

      setItin(prevItin => ({
        ...prevItin,
        documents: prevItin.documents.map(doc => doc.id === updatedDoc.id ? updatedDoc : doc),
      }));

      setInitialState({
        docTitle,
        restrictedClientIds: editModeRestrictedClientIds,
      });
    } catch (err) {
      console.error(err);
      setGlobalModalContent(() => ({
        closable: true,
        message: err?.message ?? `${t("document_failed_to_edit")} ${docData.name}`,
        buttons: [{ text: t('ok'), type: 'close' }]
      }));
    } finally {
      setSpinner(false);
    }
  };

  const handleDeleteDocument = async () => {
    setSpinner(true);
    setIsModalConfirmDeletionOpen(false);
    try {
      const { res, data, error } = await deleteDocument(itin.operatorCode, itin.passcode, docData.id, travellerId)
      if (!res.ok || error) {
        const internalErrMsg =  error || (data?.error?.length && data?.error?.[0]?.message) || data?.error || "Deleting document failed"
        console.error('Deleting document failed:', internalErrMsg)
        // eslint-disable-next-line no-throw-literal
        throw { modalMessage: `${t("document_failed_to_delete")} ${docData.name}` };
      }

      setItin(prevItin => {
        const updatedDocs = prevItin.documents.map(doc => {
          if (doc.id === folderId) {
            return {
              ...doc,
              entries: doc.entries.filter(entryId => entryId !== docData.id)
            };
          }
          return doc;
        });

        const finalDocs = updatedDocs.filter(doc => doc.id !== docData.id);
        return {
          ...prevItin,
          documents: finalDocs
        };
      });
      navigate(backButtonPath, { replace: true });

    } catch (e) {
      const internalErrMsg =  e || e?.modalMessage || e?.message || `${t("document_failed_to_delete")} ${docData.name}`
      console.error('Deleting document failed:', internalErrMsg)

      setGlobalModalContent(() => ({
        closable: true,
        message: e?.modalMessage || `${t("document_failed_to_delete")} ${docData.name}`,
        buttons: [{ text: t('ok'), type: 'close' }]
      }));
    } finally {
      setSpinner(false);
    }
  };

  const renderMyDocumentContent = () => {
    return (
      <div className={`my-document-content ${(uploadDocConfig && viewMode === "desktop") ? "no-container" : ""}`}>
        <div className="my-document-block-container">
          <span className="title"> {t('document_settings_document_title', { lng: itin.language })}</span>
          <TextInput
            className="border-grey full-width"
            value={docTitle}
            onChange={handleDocTitleChange}
          />
        </div>
        {!!filteredClients.length &&
          <div className="my-document-block-container extra-top-padding no-margin-bottom">
            <span className="title">{t('sharing', { lng: itin.language })}</span>
            <ul>
              <li tabIndex={0}>
                <div className={`item-container ${(uploadDocConfig && viewMode === "desktop") ? "black-text" : ""}`}>
                  <span>{t('document_settings_share_with_everyone', { lng: itin.language })}</span>
                  <Switch
                    checked={!uiRestrictedClientIds}
                    color="warning"
                    inputProps={{ "aria-label": "controlled", tabIndex: 0 }}
                    onChange={handleChangeShareWithEveryone}
                    onKeyDown={(e) => e.key === 'Enter' ? handleChangeShareWithEveryone() : null}
                  />
                </div>
              </li>
              {uiRestrictedClientIds && filteredClients.map((x, i) => (
                <li key={i} tabIndex={0}>
                  {
                    <div className={`item-container ${(uploadDocConfig && viewMode === "desktop") ? "black-text" : ""}`}>
                      <span>{x?.fullname}</span>
                      <Switch
                        checked={!!uiRestrictedClientIds?.includes(x.id)}
                        color="warning"
                        inputProps={{ "aria-label": "controlled", tabIndex: 0 }}
                        onChange={(e) => updateAndSyncDocRestrictions(e.target.checked, x.id)}
                        onKeyDown={(e) => e.key === 'Enter' ? updateAndSyncDocRestrictions(!e.target.checked, x.id) : null}
                      />
                    </div>
                  }
                </li>
              ))
              }
            </ul>
          </div>
        }

        {!uploadDocConfig && <button className='link' onClick={() => setIsModalConfirmDeletionOpen(true)}>{t("document_settings_delete_document_permanently", { lng: itin.language })}</button>}

        {((!uploadDocConfig && hasUnsavedChanges) || (uploadDocConfig && viewMode === "mobile")) && (
          <div className="my-document-block-container sticky-footer">
            <Button
              className="button-full-width"
              onClick={uploadDocConfig ? handleFileUpload : handleEditDocument}
              disabled={spinner}
            >
              {t("save", { lng: itin.language })}
            </Button>
          </div>
        )}

        <ModalDefault
          isOpen={isModalConfirmDeletionOpen}
          onBackdropClick={() => spinner ? null : setIsModalConfirmDeletionOpen(false)}
          className="modal-global"
          header={t("document_settings_delete_document_permanently", { lng: itin.language })}
          message={t("document_settings_delete_document_message", { lng: itin.language }).replace('%1$s', docTitle)}
          buttons={[
            <button key={0} onClick={() => setIsModalConfirmDeletionOpen(false)}>{t('cancel', { lng: itin.language })}</button>,
            <button key={1} onClick={handleDeleteDocument}>{t('confirm', { lng: itin.language })}</button>
          ]}
          loading={spinner}
        />

        {blocker.state === "blocked" && (
          <ModalDefault
            isOpen
            className="modal-global"
            message={t("unsaved_changes", { lng: itin.language })}
            buttons={[
              <button key={0} onClick={() => blocker.reset()}>{t('keep_editing', { lng: itin.language })}</button>,
              <button key={1} onClick={() => blocker.proceed()}>{t('discard_changes', { lng: itin.language })}</button>,
            ]}
          />
        )}
      </div>
    )
  }

  return (!uploadDocConfig || viewMode === 'mobile') ? (
    <Container
      className="container-my-documents"
      mainElementClassName={`documents documents-${viewMode} has-sticky-footer`}
      closePath={backButtonPath ?? `/${itin.localData.urlKey}`}
      docScreenName={uploadDocConfig ? t('document_settings_add_document', { lng: itin.language }) : docData?.name}
    >
      {spinner &&
        <Modal isOpen={spinner}>
          <div className="center-spinner">
            <Spinner />
          </div>
        </Modal>
      }

      {myDocsUploadDetails?.uploading &&
        <Modal isOpen={myDocsUploadDetails?.uploading}>
          <div className="center-spinner">
            <span className="spinner-label">{`${myDocsUploadDetails?.percent}%`}</span>
            <Spinner />
          </div>
        </Modal>
      }

      {viewMode === 'desktop' &&
        <ListTitle backButton={backButton} backButtonPath={backButtonPath} docScreenName={docData?.name} />}
      {renderMyDocumentContent()}
    </Container>
  ) : (
    renderMyDocumentContent()
  )
}

export default MyDocumentManager;
